// frontend/src/api/analytics.js
import client from "./client";

/**
 * Trigger a browser file download from a Blob response.
 *
 * @param {Blob}   blob
 * @param {string} filename
 */
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Fetch scan analytics for a single QR code.
 *
 * Returns total / period counts plus a 30-day daily breakdown.
 *
 * @param {string} code  The QR code identifier
 * @returns {Promise<{
 *   code: string,
 *   total_scans: number,
 *   scans_today: number,
 *   scans_this_week: number,
 *   scans_this_month: number,
 *   daily_counts: Array<{date: string, count: number}>
 * }>}
 */
export async function getAnalytics(code) {
  const response = await client.get(`/api/codes/${code}/analytics`);
  return response.data;
}

/**
 * Fetch the full per-code analytics bundle (KPIs + breakdown series).
 *
 * The single-code analogue of {@link getDashboardSummary}.
 *
 * @param {string} code  The QR code identifier
 * @param {number} days  Trailing window for the daily series + KPIs (1–365).
 * @returns {Promise<{
 *   code_id: number,
 *   total_scans: number,
 *   scans_last_30_days: number,
 *   scans_last_7_days: number,
 *   scans_today: number,
 *   unique_ips_last_30_days: number,
 *   scans_by_day: Array<{date: string, count: number}>,
 *   scans_by_country: Array<{country_code: string, count: number}>,
 *   scans_by_device: Array<{device_type: string, count: number}>,
 *   scans_by_hour: Array<{hour: number, count: number}>
 * }>}
 */
export async function getCodeAnalyticsSummary(code, days = 30) {
  const response = await client.get(`/api/codes/${code}/analytics/summary`, {
    params: { days },
  });
  return response.data;
}

/**
 * Fetch a device breakdown for a code, grouped by one dimension and optionally
 * narrowed by the others — the data behind the device drill-down.
 *
 * @param {string} code  The QR code identifier
 * @param {{ dimension?: string, device_type?: string, brand?: string,
 *           os?: string, browser?: string }} [opts]
 * @returns {Promise<{ dimension: string, filters: object,
 *           rows: Array<{value: string, count: number}> }>}
 */
export async function getCodeDeviceBreakdown(code, opts = {}) {
  const response = await client.get(`/api/codes/${code}/analytics/devices`, {
    params: opts,
  });
  return response.data;
}

/**
 * Fetch location heatmap data for a QR code.
 *
 * Returns up to 500 grid cells snapped to a 0.5° grid, sorted by scan
 * count descending.  Requires the org's plan to include the
 * ``location_heatmap`` entitlement (Pro+); the API returns 402 otherwise.
 *
 * @param {string} code  The QR code identifier
 * @returns {Promise<Array<{lat: number, lng: number, count: number}>>}
 */
export async function getHeatmap(code) {
  const response = await client.get(`/api/codes/${code}/analytics/heatmap`);
  return response.data;
}

/**
 * Export scan history for a QR code as CSV and trigger a browser download.
 *
 * Downloaded file is named ``<code>-scans.csv`` and contains columns:
 * code, scanned_at, ip_address, user_agent.
 *
 * @param {string} code  The QR code identifier
 * @returns {Promise<void>}
 */
export async function exportScans(code) {
  const response = await client.get(`/api/codes/${code}/scans/export`, {
    responseType: "blob",
  });
  triggerDownload(response.data, `${code}-scans.csv`);
}

/**
 * Fetch aggregated dashboard statistics for the authenticated admin.
 *
 * @returns {Promise<{
 *   total_codes: number,
 *   active_codes: number,
 *   total_scans: number,
 *   top_codes: Array<{code: string, scan_count: number}>,
 *   recent_scans: Array<{code: string, scanned_at: string, ip_address: string|null}>
 * }>}
 */
export async function getDashboard() {
  const response = await client.get("/api/dashboard");
  return response.data;
}

/**
 * Fetch the full dashboard analytics bundle (KPIs + breakdown series).
 *
 * @param {number}  days     Trailing window for the daily series + KPIs (1–365).
 * @param {boolean} compare  When true, include a period-over-period comparison block.
 * @returns {Promise<{
 *   days: number,
 *   summary: {
 *     total_scans: number,
 *     scans_last_30_days: number,
 *     scans_last_7_days: number,
 *     scans_today: number,
 *     unique_ips_last_30_days: number,
 *     active_codes_count: number,
 *     top_code: {code_id: number, name: string|null, scan_count: number}|null
 *   },
 *   scans_by_day: Array<{date: string, count: number}>,
 *   scans_by_country: Array<{country_code: string, count: number}>,
 *   scans_by_device: Array<{device_type: string, count: number}>,
 *   scans_by_hour: Array<{hour: number, count: number}>,
 *   comparison: {
 *     previous_scans_in_period: number,
 *     previous_unique_ips: number,
 *     deltas: {scans_pct_change: number|null, unique_pct_change: number|null}
 *   }|null
 * }>}
 */
export async function getDashboardSummary(days = 30, compare = false) {
  const params = { days };
  if (compare) params.compare = true;
  const response = await client.get("/api/dashboard/summary", { params });
  return response.data;
}

/**
 * Fetch UTM source / medium / campaign breakdown for the active org.
 *
 * @param {number} days  Trailing window in days (1–365, default 30).
 * @returns {Promise<{
 *   sources:   Array<{utm_source: string, count: number}>,
 *   mediums:   Array<{utm_medium: string, count: number}>,
 *   campaigns: Array<{utm_campaign: string, count: number}>
 * }>}
 */
export async function getUTMBreakdown(days = 30) {
  const response = await client.get("/api/analytics/utm", { params: { days } });
  return response.data;
}

/**
 * Export scan data as CSV and trigger a browser download.
 *
 * Requires Pro+ plan; throws a 402 AxiosError otherwise (the global
 * client interceptor will already have fired the plan-limit event).
 *
 * @param {string} fromDate  ISO date string ``YYYY-MM-DD`` (inclusive start).
 * @param {string} toDate    ISO date string ``YYYY-MM-DD`` (inclusive end).
 * @returns {Promise<void>}
 */
export async function exportOrgScans(fromDate, toDate) {
  const response = await client.get("/api/analytics/export", {
    params: { from: fromDate, to: toDate },
    responseType: "blob",
  });
  const filename = `scans_${fromDate}_${toDate}.csv`;
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Fetch the current user's scheduled analytics report configuration.
 *
 * @returns {Promise<{
 *   id: string,
 *   frequency: string,
 *   day_of_week: number|null,
 *   day_of_month: number|null,
 *   last_sent_at: string|null,
 *   is_active: boolean,
 *   created_at: string
 * }|null>}  Returns null when no schedule has been set.
 */
export async function getScheduledReport() {
  const response = await client.get("/api/analytics/reports/schedule");
  return response.data;
}

/**
 * Create or update the current user's scheduled analytics report.
 *
 * @param {{ frequency: string, day_of_week?: number, day_of_month?: number }} body
 * @returns {Promise<object>} The saved schedule row.
 */
export async function upsertScheduledReport(body) {
  const response = await client.post("/api/analytics/reports/schedule", body);
  return response.data;
}

/**
 * Delete the current user's scheduled analytics report.
 *
 * Idempotent — returns 204 whether or not a schedule existed.
 *
 * @returns {Promise<void>}
 */
export async function deleteScheduledReport() {
  await client.delete("/api/analytics/reports/schedule");
}
